import type { ReactNode } from "react";
import { Inbox, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "../Button";

export function LoadingState({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 rounded-sm border border-border bg-bg3 animate-pulse" />
      ))}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("rounded-sm border border-border bg-bg3 animate-pulse", className)} />;
}

export function EmptyState({
  title,
  message,
  icon,
  action,
}: {
  title: string;
  message?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-5 py-12">
      <div className="flex items-center justify-center h-11 w-11 rounded-lg border border-border bg-bg3 text-text3 mb-3">
        {icon ?? <Inbox className="h-5 w-5" />}
      </div>
      <h3 className="text-sm font-semibold text-text mb-1">{title}</h3>
      {message && <p className="text-xs text-text2 max-w-xs">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-5 py-12">
      <div className="flex items-center justify-center h-11 w-11 rounded-lg border border-danger/25 bg-danger-dim text-danger mb-3">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <h3 className="text-sm font-semibold text-text mb-1">Une erreur est survenue</h3>
      <p className="text-xs text-text2 max-w-xs">{message ?? "Impossible de charger les données."}</p>
      {onRetry && (
        <Button variant="ghost" size="sm" className="mt-4" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}
